import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { catchError, finalize, of } from 'rxjs';

import { AuthService } from '@/core/auth/auth.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { ComissaoFrequenciaOpcaoResponse, FrequenciaComissaoResponse, FrequenciaMembroResponse } from '@/core/api/sicom-api/comissoes/frequencia/frequencia-comissao.model';
import { FrequenciaComissaoService } from '@/core/api/sicom-api/comissoes/frequencia/frequencia-comissao.service';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { ColunaExportacaoCsv, exportarLinhasCsv } from '@/shared/utils/exportar-csv.util';

import { PainelComissoesContainer } from '../painel-comissoes/painel-comissoes';
import { formatarDataReuniao, FrequenciaMatrizComponent } from './frequencia-matriz';

/** Consulta a frequência (presença) dos membros de uma comissão por ano/UF, com matriz e exportação CSV. */
@Component({
    selector: 'app-frequencia-comissoes',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, SelectModule, SkeletonModule, SelectEstado, PainelComissoesContainer, FrequenciaMatrizComponent, CanDirective],
    templateUrl: './frequencia-comissoes.html',
    styleUrls: ['./frequencia-comissoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrequenciaComissoesComponent implements OnInit {
    private readonly frequenciaService = inject(FrequenciaComissaoService);
    private readonly authService = inject(AuthService);

    /** Seção é read-only (rankings/frequência); o backend exige apenas `estatisticas.visualizar`. */
    protected readonly rolePermissaoVisualizar = PERMISSOES.comissoes.estatisticas.visualizar;

    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    private ufTokenRestrita: string | null = null;

    ano: number = new Date().getFullYear();
    readonly comissoes = signal<ComissaoFrequenciaOpcaoResponse[]>([]);
    idEstruturaSelecionada: number | null = null;

    readonly carregandoComissoes = signal(false);
    readonly carregando = signal(false);
    readonly gerado = signal(false);
    readonly erro = signal('');

    readonly frequencia = signal<FrequenciaComissaoResponse | null>(null);

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.carregarComissoes();
    }

    onUfChange(uf: string): void {
        this.ufSelecionada = this.normalizarUf(uf);
        this.reiniciarResultado();
        this.carregarComissoes();
    }

    onAnoChange(): void {
        if (!this.ano) {
            return;
        }
        this.reiniciarResultado();
        this.carregarComissoes();
    }

    carregarComissoes(): void {
        if (!this.ano) {
            this.comissoes.set([]);
            return;
        }
        this.carregandoComissoes.set(true);
        this.frequenciaService
            .listarComissoes(this.ano, this.ufSelecionada)
            .pipe(
                catchError(() => of<ComissaoFrequenciaOpcaoResponse[]>([])),
                finalize(() => this.carregandoComissoes.set(false))
            )
            .subscribe((lista) => {
                this.comissoes.set(lista ?? []);
                if (!this.comissoes().some((comissao) => comissao.idEstrutura === this.idEstruturaSelecionada)) {
                    this.idEstruturaSelecionada = null;
                }
            });
    }

    gerar(): void {
        if (!this.idEstruturaSelecionada || !this.ano) {
            return;
        }
        this.carregando.set(true);
        this.gerado.set(true);
        this.erro.set('');
        this.frequencia.set(null);
        this.frequenciaService
            .obterFrequencia(this.idEstruturaSelecionada, this.ano)
            .pipe(
                catchError(() => {
                    this.erro.set('Não foi possível carregar a frequência da comissão selecionada.');
                    return of(null);
                }),
                finalize(() => this.carregando.set(false))
            )
            .subscribe((resultado) => {
                this.frequencia.set(resultado);
            });
    }

    exportarCsv(): void {
        const frequencia = this.frequencia();
        if (!frequencia || !frequencia.membros?.length) {
            return;
        }
        const reunioes = frequencia.reunioes ?? [];
        const colunas: ColunaExportacaoCsv<FrequenciaMembroResponse>[] = [
            { cabecalho: 'MEMBRO', valor: (membro) => membro.nome },
            { cabecalho: 'PARTICIPAÇÃO', valor: (membro) => membro.dsParticipacao },
            ...reunioes.map((reuniao) => ({
                cabecalho: formatarDataReuniao(reuniao.dhInicioReuniao),
                valor: (membro: FrequenciaMembroResponse) => membro.presencas?.find((presenca) => presenca.idReuniao === reuniao.idReuniao)?.presenca ?? ''
            })),
            { cabecalho: 'PRESENÇAS', valor: (membro) => membro.totalPresencas },
            { cabecalho: '%', valor: (membro) => membro.percentualPresenca }
        ];
        exportarLinhasCsv(`frequencia-${frequencia.descricaoComissao}-${this.ano}`, colunas, frequencia.membros);
    }

    private reiniciarResultado(): void {
        this.idEstruturaSelecionada = null;
        this.frequencia.set(null);
        this.gerado.set(false);
        this.erro.set('');
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        if (/^[A-Z]{2}$/.test(ufToken) && ufToken !== 'BR') {
            this.ufTokenRestrita = ufToken;
            this.ufsPermitidas = [ufToken];
            this.ufSelecionada = ufToken;
        } else {
            this.ufTokenRestrita = null;
            this.ufsPermitidas = null;
            this.ufSelecionada = null;
        }
    }

    private normalizarUf(uf?: string | null): string | null {
        const valor = String(uf ?? '')
            .trim()
            .toUpperCase();
        if (!valor || valor === 'BR' || !/^[A-Z]{2}$/.test(valor)) {
            return this.ufTokenRestrita;
        }
        if (this.ufTokenRestrita && valor !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }
        return valor;
    }
}
